import React, { useEffect, useState, useCallback } from "react";
import { Button, Input, Modal, Select, Spin, Tag, Typography, Upload } from "antd";
import {
  AlertOutlined,
  ExperimentOutlined,
  NotificationOutlined,
  ShopOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { toast } from "../../components/toasts/toastStore";
import { PATH } from "../../redux/utils";
import {
  getAvisos, crearAviso, editarAviso, eliminarAviso,
  getConfiguracionEmpresa, putConfiguracionEmpresa,
  getCatalogoSimple, crearCatalogoSimple, eliminarCatalogoSimple,
} from "./configuracionApi";

import "./ConfiguracionPage.css";
import { usePermisos } from "../../context/PermisosContext";

const { Title, Text } = Typography;

const TIPOS_AVISO = [
  { value: "app", label: "Aplicación" },
  { value: "login", label: "Pantalla de inicio de sesión" },
];

const logoSrc = (path) => (path ? (path.startsWith("http") ? path : `${PATH}${path}`) : null);

// ── Panel Avisos ─────────────────────────────────────────────────────────

function PanelAvisos() {
  const { perm } = usePermisos() || { perm: () => true };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [descripcion, setDescripcion] = useState("");
  const [tipo, setTipo] = useState("app");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getAvisos()
      .then((res) => setItems(res.data?.items ?? res.data ?? []))
      .catch(() => toast.error("No se pudieron cargar los avisos"))
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    const valor = descripcion.trim();
    if (!valor) return;
    setSaving(true);
    try {
      const res = await crearAviso({ descripcion: valor, tipo, active: 1 });
      setItems((prev) => [...prev, res.data?.item ?? res.data]);
      setDescripcion("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo agregar el aviso");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (aviso) => {
    const nuevoActive = aviso.active ? 0 : 1;
    setItems((prev) => prev.map((a) => (a.id_aviso === aviso.id_aviso ? { ...a, active: nuevoActive } : a)));
    try {
      await editarAviso(aviso.id_aviso, { descripcion: aviso.descripcion, tipo: aviso.tipo, active: nuevoActive });
    } catch (err) {
      setItems((prev) => prev.map((a) => (a.id_aviso === aviso.id_aviso ? { ...a, active: aviso.active } : a)));
      toast.error(err?.response?.data?.detail || "No se pudo actualizar el aviso");
    }
  };

  const handleDelete = (aviso) => {
    Modal.confirm({
      title: "Eliminar aviso",
      content: `¿Seguro que deseas eliminar este aviso?`,
      okText: "Eliminar",
      cancelText: "Cancelar",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await eliminarAviso(aviso.id_aviso);
          setItems((prev) => prev.filter((a) => a.id_aviso !== aviso.id_aviso));
        } catch (err) {
          toast.error(err?.response?.data?.detail || "No se pudo eliminar el aviso");
        }
      },
    });
  };

  return (
    <div className="cfg-panel-body">
      <div className="cfg-block">
        <div className="cfg-block-header">
          <AlertOutlined className="cfg-block-icon" />
          <div>
            <div className="cfg-block-title">Avisos</div>
            <div className="cfg-block-desc">Mensajes que se muestran en la aplicación o en la pantalla de inicio de sesión.</div>
          </div>
        </div>

        <div className="cfg-tipo-add-row">
          <Input
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            placeholder="Escribe el mensaje del aviso..."
            className="cfg-tipo-input"
            onPressEnter={handleAdd}
            maxLength={255}
          />
          <Select
            value={tipo}
            onChange={setTipo}
            style={{ width: 200 }}
            options={TIPOS_AVISO}
          />
          {perm("configuracion", "insertar") && (
            <Button
            type="primary"
            icon={<i className="fa-solid fa-plus" />}
            onClick={handleAdd}
            loading={saving}
            disabled={!descripcion.trim()}
            className="cfg-tipo-btn-add"
          >
            Agregar
          </Button>
          )}
        </div>

        {loading ? (
          <div className="cfg-loading-row"><Spin size="small" /></div>
        ) : items.length === 0 ? (
          <div className="cfg-tipo-empty">Sin avisos configurados</div>
        ) : (
          <div className="cfg-aviso-list">
            {items.map((a) => (
              <div key={a.id_aviso} className="cfg-aviso-item" style={!a.active ? { opacity: 0.55 } : undefined}>
                <span className="cfg-aviso-item-text">
                  {a.descripcion}
                  <Tag style={{ marginLeft: 8 }}>{TIPOS_AVISO.find((t) => t.value === a.tipo)?.label || a.tipo}</Tag>
                </span>
                {perm("configuracion", "editar") && (
                  <Button size="small" onClick={() => handleToggleActive(a)}>
                  {a.active ? "Desactivar" : "Activar"}
                </Button>
                )}
                {perm("configuracion", "eliminar") && (
                  <Button icon={<i className="fa-solid fa-trash" />} className="cd-btn-delete" onClick={() => handleDelete(a)}>Eliminar</Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Panel Empresa ────────────────────────────────────────────────────────

function PanelEmpresa() {
  const { perm } = usePermisos() || { perm: () => true };
  const [empresa, setEmpresa] = useState({ nombre_empresa: "", logo: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editando, setEditando] = useState(false);
  const [nombreDraft, setNombreDraft] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const fetchEmpresa = useCallback(() => {
    setLoading(true);
    getConfiguracionEmpresa()
      .then((res) => setEmpresa({
        nombre_empresa: res.data?.nombre_empresa || "",
        logo: res.data?.logo || res.data?.path || null,
      }))
      .catch(() => toast.error("No se pudo cargar la configuración de la empresa"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchEmpresa(); }, [fetchEmpresa]);

  const handleEditar = () => {
    setNombreDraft(empresa.nombre_empresa || "");
    setLogoFile(null);
    setLogoPreview(null);
    setEditando(true);
  };

  const handleCancelar = () => {
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(null);
    setLogoPreview(null);
    setEditando(false);
  };

  const handleLogoChange = (file) => {
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    return false;
  };

  const handleGuardar = async () => {
    if (!nombreDraft.trim()) return toast.warning("El nombre de la empresa es obligatorio");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("nombre_empresa", nombreDraft.trim());
      if (logoFile) fd.append("logo", logoFile);
      await putConfiguracionEmpresa(fd);
      toast.success("Datos de la empresa guardados");
      handleCancelar();
      fetchEmpresa();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cfg-panel-body">
      <div className="cfg-block">
        <div className="cfg-block-header">
          <ShopOutlined className="cfg-block-icon" />
          <div>
            <div className="cfg-block-title">Datos de la empresa</div>
            <div className="cfg-block-desc">Nombre y logo de la clínica, usados en el sidebar y en reportes.</div>
          </div>
        </div>

        {loading ? (
          <div className="cfg-loading-row"><Spin size="small" /></div>
        ) : !editando ? (
          <div className="cfg-empresa-card">
            <div className={`cfg-empresa-logo-zone cfg-empresa-logo-zone--view${empresa.logo ? " cfg-empresa-logo-zone--filled" : ""}`}>
              {empresa.logo ? (
                <img src={logoSrc(empresa.logo)} alt="Logo empresa" className="cfg-empresa-logo-img" />
              ) : (
                <div className="cfg-empresa-logo-empty">
                  <ShopOutlined className="cfg-empresa-logo-empty-icon" />
                  <span>Sin logo</span>
                </div>
              )}
            </div>
            <div className="cfg-empresa-fields">
              <div className="cfg-empresa-view-nombre">
                {empresa.nombre_empresa || <span className="cfg-empresa-view-vacio">Sin nombre configurado</span>}
              </div>
              {perm("configuracion", "editar") && (
                <button type="button" className="cfg-empresa-edit-btn" onClick={handleEditar}>
                Editar
              </button>
              )}
            </div>
          </div>
        ) : (
          <div className="cfg-empresa-card">
            <Upload accept="image/*" showUploadList={false} beforeUpload={handleLogoChange}>
              <div className={`cfg-empresa-logo-zone${(logoPreview || empresa.logo) ? " cfg-empresa-logo-zone--filled" : ""}`}>
                {(logoPreview || empresa.logo) ? (
                  <img src={logoPreview || logoSrc(empresa.logo)} alt="Logo empresa" className="cfg-empresa-logo-img" />
                ) : (
                  <div className="cfg-empresa-logo-empty">
                    <UploadOutlined className="cfg-empresa-logo-empty-icon" />
                    <span>Subir logo</span>
                    <span className="cfg-empresa-logo-empty-hint">PNG · JPG · WEBP</span>
                  </div>
                )}
                <div className="cfg-empresa-logo-overlay">
                  <UploadOutlined />
                  <span>{empresa.logo ? "Cambiar" : "Subir"}</span>
                </div>
              </div>
            </Upload>

            <div className="cfg-empresa-fields">
              <div className="cfg-empresa-field">
                <label className="cfg-empresa-label">Nombre de la empresa</label>
                <Input
                  value={nombreDraft}
                  onChange={(e) => setNombreDraft(e.target.value)}
                  placeholder="Visum Oftalmología"
                  className="cfg-empresa-input"
                  maxLength={150}
                  size="large"
                />
              </div>
              {logoFile && (
                <div className="cfg-empresa-file-badge">
                  <UploadOutlined style={{ fontSize: 12 }} />
                  {logoFile.name}
                </div>
              )}
              <div className="cfg-empresa-actions">
                <Button type="primary" onClick={handleGuardar} loading={saving} className="cfg-tipo-btn-add">
                  Guardar cambios
                </Button>
                <button type="button" className="cfg-empresa-cancel-btn" onClick={handleCancelar}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Panel Catálogos (opciones de los selects del expediente) ─────────────

const CATALOGOS = [
  {
    clave: "tipos-estudio",
    titulo: "Tipos de estudio",
    desc: "Estudios diagnósticos disponibles en el expediente del paciente.",
    icon: <ExperimentOutlined className="cfg-block-icon" />,
    placeholder: "Nombre del tipo de estudio...",
  },
];

function BloqueCatalogo({ clave, titulo, desc, icon, placeholder }) {
  const { perm } = usePermisos() || { perm: () => true };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nombre, setNombre] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getCatalogoSimple(clave)
      .then((res) => setItems(res.data?.items ?? []))
      .catch(() => toast.error(`No se pudo cargar: ${titulo}`))
      .finally(() => setLoading(false));
  }, [clave, titulo]);

  const handleAdd = async () => {
    const valor = nombre.trim();
    if (!valor) return;
    setSaving(true);
    try {
      const res = await crearCatalogoSimple(clave, valor);
      setItems((prev) => [...prev, res.data.item].sort((a, b) => a.nombre.localeCompare(b.nombre, "es")));
      setNombre("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo agregar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (item) => {
    Modal.confirm({
      title: `Eliminar "${item.nombre}"`,
      content: "Dejará de aparecer en los selects. Los registros que ya lo usan no se modifican.",
      okText: "Eliminar",
      cancelText: "Cancelar",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await eliminarCatalogoSimple(clave, item.id);
          setItems((prev) => prev.filter((i) => i.id !== item.id));
        } catch (err) {
          toast.error(err?.response?.data?.detail || "No se pudo eliminar");
        }
      },
    });
  };

  return (
    <div className="cfg-block">
      <div className="cfg-block-header">
        {icon}
        <div>
          <div className="cfg-block-title">{titulo}</div>
          <div className="cfg-block-desc">{desc}</div>
        </div>
      </div>

      <div className="cfg-tipo-add-row">
        <Input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder={placeholder}
          className="cfg-tipo-input"
          onPressEnter={handleAdd}
          maxLength={150}
        />
        {perm("configuracion", "insertar") && (
          <Button
          type="primary"
          icon={<i className="fa-solid fa-plus" />}
          onClick={handleAdd}
          loading={saving}
          disabled={!nombre.trim()}
          className="cfg-tipo-btn-add"
        >
          Agregar
        </Button>
        )}
      </div>

      {loading ? (
        <div className="cfg-loading-row"><Spin size="small" /></div>
      ) : items.length === 0 ? (
        <div className="cfg-tipo-empty">Sin opciones configuradas</div>
      ) : (
        <div className="cfg-tipo-chips">
          {items.map((item) => (
            <span key={item.id} className="cfg-tipo-chip">
              <span className="cfg-tipo-chip-label">{item.nombre}</span>
              {perm("configuracion", "eliminar") && (
                <button type="button" className="cfg-tipo-chip-del" onClick={() => handleDelete(item)} aria-label={`Eliminar ${item.nombre}`}>
                <i className="fa-regular fa-trash-can" />
              </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function PanelCatalogos() {
  return (
    <div className="cfg-panel-body">
      {CATALOGOS.map((c) => <BloqueCatalogo key={c.clave} {...c} />)}
    </div>
  );
}

// ── Módulos del sidebar interno de Configuración ──────────────────────────

const MODULES = [
  {
    key: "avisos",
    label: "Avisos",
    icon: <i className="fa-solid fa-bullhorn" />,
    color: "var(--brand-primary, #5778c3)",
    bg: "var(--brand-primary, #5778c3)",
  },
  {
    key: "empresa",
    label: "Empresa",
    icon: <i className="fa-solid fa-shop" />,
    color: "var(--brand-primary, #5778c3)",
    bg: "var(--brand-primary, #5778c3)",
  },
  {
    key: "catalogos",
    label: "Catálogos",
    icon: <i className="fa-solid fa-list-check" />,
    color: "var(--brand-primary, #5778c3)",
    bg: "var(--brand-primary, #5778c3)",
  },
];

const PANELS = {
  avisos: PanelAvisos,
  empresa: PanelEmpresa,
  catalogos: PanelCatalogos,
};

export default function ConfiguracionPage() {
  const [active, setActive] = useState("avisos");
  const activeModule = MODULES.find((m) => m.key === active);

  return (
    <main className="cfg-main">
      <div className="cfg-layout">

        <aside className="cfg-sidebar">
          <div className="cfg-sidebar-header">
            <Title level={5} className="cfg-sidebar-title">Configuración</Title>
          </div>

          <nav className="cfg-nav">
            {MODULES.map((mod) => (
              <button
                key={mod.key}
                type="button"
                className={`cfg-nav-item${active === mod.key ? " cfg-nav-item--active" : ""}`}
                onClick={() => setActive(mod.key)}
                style={active === mod.key ? { "--mod-color": mod.color } : {}}
              >
                <span
                  className="cfg-nav-icon"
                  style={{ background: active === mod.key ? mod.bg : undefined }}
                >
                  {mod.icon}
                </span>
                <span className="cfg-nav-label">{mod.label}</span>
                {active === mod.key && <span className="cfg-nav-active-bar" />}
              </button>
            ))}
          </nav>
        </aside>

        <section className="cfg-content">
          <div className="cfg-content-card">
            <div className="cfg-content-header">
              <span className="cfg-content-icon" style={{ background: activeModule?.bg }}>
                {activeModule?.icon}
              </span>
              <div>
                <Title level={3} className="cfg-content-title">{activeModule?.label}</Title>
                <Text type="secondary" className="cfg-content-subtitle">Configuración del módulo</Text>
              </div>
            </div>
            <div className="cfg-content-inner-divider" />
            {React.createElement(PANELS[active] || PanelAvisos)}
          </div>
        </section>

      </div>
    </main>
  );
}
